import ResidentDetail from "./residentDetail";
import EventBooking from "./eventBooking";
import {EventBookingStatus} from "../../../../shared/enums/eventBookingStatus";

export default class ResidentBookingGroup {
  /**
   * PlatformId for the main resident
   * (With +1 booking, this is always the creator)
   */
  mainResidentPlatformId: string;

  /**
   * Name for the main resident
   * (With +1 booking, this is always the creator)
   */
  mainResidentName?: string;

  /**
   * Unit code for the booking group
   * (With +1 booking, both parties always share unit code)
   */
  unitCode?: string;

  /**
   * Phone number for the main resident
   * (With +1 booking, this is always the creator)
   */
  mainResidentPhoneNumber?: string;

  /**
   * The state for this booking group
   * (With +1 booking, both bookings will always have same status)
   */
  bookingStatus?: EventBookingStatus;

  /**
   * The required capacity for confirming this booking
   */
  requiredCapacity: number;

  /**
   * The state for this booking group if it is booked by townhouse buddy
   */
  isBookingOnBehalf: boolean;

  /**
   * The person type of the main resident, used to show which area they live in
   */
  personTypeId?: string;

  public constructor(
    mainResidentId: string,
    requiredCapacity: number,
    mainResidentName?: string,
    unitCode?: string,
    mainResidentPhoneNumber?: string,
    isBookingOnBehalf: boolean = false,
    personTypeId?: string
  ) {
    this.mainResidentPlatformId = mainResidentId;
    this.requiredCapacity = requiredCapacity;
    this.mainResidentName = mainResidentName;
    this.unitCode = unitCode;
    this.mainResidentPhoneNumber = mainResidentPhoneNumber;
    this.isBookingOnBehalf = isBookingOnBehalf;
    this.personTypeId = personTypeId;
  }

  /**
   * Populate a booking group from resident details
   * @param detail The resident to display
   * @returns The booking group
   */
  public static fromResidentDetail(detail: ResidentDetail) {
    return new ResidentBookingGroup(
      detail.platformUserId,
      1,
      `${detail.preferredName} ${detail.lastName}`,
      detail.unitCode,
      detail.mobileNumber ?? detail.phoneNumber,
      false,
      detail.personTypeId
    );
  }

  /**
   * Populate a booking group from resident details, who has a booking
   * @param detail The resident detail to display
   * @param eventBooking The booking for the resident
   */
  public static withEventBooking(
    detail: ResidentDetail,
    eventBooking: EventBooking
  ): ResidentBookingGroup {
    const bookingGroup = this.fromResidentDetail(detail);
    bookingGroup.bookingStatus = eventBooking.status;
    return bookingGroup;
  }

  /**
   * Return multiple booking groups from a set of bookings
   * @param bookings The bookings related to the resident
   * @returns The primary and secondary resident details
   */
  public static fromEventBookingGroup(
    bookings: EventBooking[]
  ): ResidentBookingGroup[] {
    if (bookings.length !== 2) {
      // We don't have a manageable set of bookings, so we can not populate a booking group
      return [];
    }

    const primaryBooking = bookings.find((b) => b.creatorId === b.personId);
    if (!primaryBooking) {
      // Could not correctly identify the primary booking, can not populate a booking group
      return [];
    }

    const primaryResidentBooking =
      this.createPrimaryResidentBookingGroup(primaryBooking);
    const secondaryResidentBookings =
      this.createSecondaryResidentBookingGroups(bookings);
    return [primaryResidentBooking, ...secondaryResidentBookings];
  }

  private static createPrimaryResidentBookingGroup(
    booking: EventBooking
  ): ResidentBookingGroup {
    const primaryResidentBookingGroup = new ResidentBookingGroup(
      booking.personId,
      1,
      booking.residentData.name,
      booking.residentData.unitCode,
      booking.residentData.phoneNumber,
      false,
      booking.residentData.personTypeId
    );
    primaryResidentBookingGroup.bookingStatus = booking.status;
    return primaryResidentBookingGroup;
  }

  private static createSecondaryResidentBookingGroups(
    bookings: EventBooking[]
  ): ResidentBookingGroup[] {
    const secondaryResidentBookings = bookings.filter(
      (b) => b.creatorId !== b.personId
    );
    const secondaryResidentBookingGroups = secondaryResidentBookings.map(
      (secondaryResidentBooking) => {
        const secondaryResidentBookingGroup = new ResidentBookingGroup(
          secondaryResidentBooking.personId,
          1,
          secondaryResidentBooking.residentData.name,
          secondaryResidentBooking.residentData.unitCode,
          secondaryResidentBooking.residentData.phoneNumber,
          true,
          secondaryResidentBooking.residentData.personTypeId
        );
        secondaryResidentBookingGroup.bookingStatus =
          secondaryResidentBooking.status;
        return secondaryResidentBookingGroup;
      }
    );
    return secondaryResidentBookingGroups;
  }
}
